/**
 * IntegrationSettingsLayout
 *
 * Shared layout shell for all per-integration settings pages.
 * Provides: header with logo/name/status, back button, save button, last-sync badge.
 * Each settings page wraps its sections in this component.
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CircleCheck as CheckCircle2, Clock, Loader as Loader2, Save, Plug, CircleAlert as AlertCircle } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IntegrationInfo {
  id:           string;
  provider_name: string;
  credentials:  Record<string, string>;
  settings:     Record<string, unknown>;
  is_active:    boolean;
  last_sync_at: string | null;
}

interface Props {
  /** Provider display label, e.g. "Shopify (חיבור ידני)" */
  providerLabel: string;
  /** Emoji or logo text */
  logoText:      string;
  /** Tailwind bg class for logo bg, e.g. "bg-green-50" */
  logoBg:        string;
  /** The current integration record (null if not yet loaded) */
  integration:   IntegrationInfo | null;
  /** Whether the outer page is loading the integration record */
  loading:       boolean;
  /** Whether a save is in progress */
  saving:        boolean;
  /** Error message to show at the top */
  error:         string | null;
  /** Called when "שמור הגדרות" is clicked */
  onSave:        () => void;
  /** Called when "עדכן הגדרות חיבור" is clicked (opens parent modal) */
  onEditCredentials?: () => void;
  children:      ReactNode;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('he-IL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// ─── Component ────────────────────────────────────────────────────────────────

export function IntegrationSettingsLayout({
  providerLabel,
  logoText,
  logoBg,
  integration,
  loading,
  saving,
  error,
  onSave,
  onEditCredentials,
  children,
}: Props) {
  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-12" dir="rtl">

      {/* ── Back nav ── */}
      <Link
        to="/settings/integrations"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
      >
        <ArrowRight className="w-4 h-4" />
        חזרה לממשקים וחיבורים
      </Link>

      {/* ── Header card ── */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 flex items-center justify-between gap-4 shadow-sm">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shadow-sm ${logoBg}`}>
            {logoText}
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{providerLabel}</h1>
            <div className="flex items-center gap-3 mt-1">
              {loading ? (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> טוען…
                </span>
              ) : integration?.is_active ? (
                <>
                  <span className="flex items-center gap-1 text-xs font-semibold text-green-700
                                   bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3" />
                    מחובר
                  </span>
                  {integration.last_sync_at && (
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      סנכרון: {fmtDateTime(integration.last_sync_at)}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-xs text-amber-600 font-medium">לא מחובר</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {onEditCredentials && (
            <button
              onClick={onEditCredentials}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600
                         border border-gray-200 bg-white hover:bg-gray-50 rounded-xl transition-colors"
            >
              <Plug className="w-3.5 h-3.5" />
              עדכן חיבור
            </button>
          )}
          <button
            onClick={onSave}
            disabled={saving || loading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700
                       disabled:opacity-50 disabled:cursor-not-allowed
                       text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
          >
            {saving
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Save className="w-4 h-4" />
            }
            שמור הגדרות
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading ? (
        <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">טוען הגדרות…</span>
        </div>
      ) : (
        <div className="space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Settings Section card ─────────────────────────────────────────────────────

interface SectionProps {
  title:       string;
  description?: string;
  icon?:       ReactNode;
  children:    ReactNode;
}

export function SettingsSection({ title, description, icon, children }: SectionProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
        {icon && (
          <div className="p-2 bg-gray-50 rounded-xl">
            {icon}
          </div>
        )}
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="px-6 py-5 space-y-4">
        {children}
      </div>
    </div>
  );
}

// ─── Toggle row ────────────────────────────────────────────────────────────────

interface ToggleRowProps {
  label:        string;
  description?: string;
  checked:      boolean;
  onChange:     (v: boolean) => void;
  disabled?:    boolean;
}

export function ToggleRow({ label, description, checked, onChange, disabled }: ToggleRowProps) {
  return (
    <label className={`flex items-start justify-between gap-4 py-3 border-b border-gray-50 last:border-0
                       ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer group'}`}>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium transition-colors
                       ${checked && !disabled ? 'text-gray-900' : 'text-gray-700'}`}>
          {label}
        </p>
        {description && (
          <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{description}</p>
        )}
      </div>
      <div className="shrink-0 mt-0.5">
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          disabled={disabled}
          onClick={() => !disabled && onChange(!checked)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none
                      focus:ring-2 focus:ring-primary-400 focus:ring-offset-1
                      ${checked ? 'bg-primary-600' : 'bg-gray-200'}`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform
                        ${checked ? '-translate-x-4.5' : '-translate-x-1'}`}
            style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }}
          />
        </button>
      </div>
    </label>
  );
}

// ─── Select row ────────────────────────────────────────────────────────────────

interface SelectRowProps {
  label:        string;
  description?: string;
  value:        string;
  options:      { value: string; label: string }[];
  onChange:     (v: string) => void;
  disabled?:    boolean;
}

export function SelectRow({ label, description, value, options, onChange, disabled }: SelectRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-50 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {description && (
          <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{description}</p>
        )}
      </div>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="shrink-0 text-sm border border-gray-200 rounded-lg px-2.5 py-1.5
                   bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-300
                   disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Number input row ─────────────────────────────────────────────────────────

interface NumberRowProps {
  label:        string;
  description?: string;
  value:        number;
  min?:         number;
  max?:         number;
  suffix?:      string;
  onChange:     (v: number) => void;
  disabled?:    boolean;
}

export function NumberRow({ label, description, value, min, max, suffix, onChange, disabled }: NumberRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-50 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {description && (
          <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{description}</p>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          onChange={e => {
            const n = parseFloat(e.target.value);
            if (!isNaN(n)) onChange(n);
          }}
          disabled={disabled}
          className="w-20 text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-center
                     bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-300
                     disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {suffix && <span className="text-xs text-gray-500">{suffix}</span>}
      </div>
    </div>
  );
}
